import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import FloatingProgressBar from 'components/FloatingProgressBar/FloatingProgressBar';

const PDF_DOWNLOAD_TYPES = ['call_logs', 'case_notes', 'secure_message', 'case_notes_call_logs'];

const isPdfDownloadType = (type) => PDF_DOWNLOAD_TYPES.includes(type);

const normalizeFolderDownload = (download, downloadProgress, onRemoveDownload) => {
  const blobProgress = downloadProgress[download.id];

  let phase;
  if (download.status === 'done') {
    phase = 'done';
  } else if (download.status === 'failed') {
    phase = 'failed';
  } else if (blobProgress?.isDownloading || download.status === 'completed') {
    phase = 'downloading';
  } else {
    phase = 'preparing';
  }

  const isPdf = isPdfDownloadType(download.type);
  const isCaseDownload = download.type === 'download_case';
  const isChronologyExhibits = download.type === 'chronology_exhibits';

  return {
    key: download.id,
    id: download.id,
    phase,
    label: download.folderName,
    fileName: blobProgress?.fileName ?? download.zipFile?.fileName,
    progress: blobProgress?.progress ?? 0,
    loaded: blobProgress?.loaded,
    total: blobProgress?.total,
    message:
      download.preparingMessage ||
      (isChronologyExhibits
        ? 'Preparing chronology exhibits...'
        : isPdf
        ? 'Preparing PDF...'
        : isCaseDownload
        ? 'Case download is being processed'
        : 'Preparing zip...'),
    successHeadline: isChronologyExhibits
      ? 'Chronology exhibits downloaded successfully'
      : isPdf
      ? 'PDF downloaded successfully'
      : isCaseDownload
      ? 'Case files downloaded successfully'
      : 'Folder downloaded successfully',
    error: typeof download.error === 'string' ? download.error : download.error?.message,
    isDismissing: download.isDismissing,
    remove: onRemoveDownload,
  };
};

const normalizeFileDownload = (download, onRemoveFileDownload) => {
  let phase;
  if (download.downloadSuccess) {
    phase = 'done';
  } else if (download.error) {
    phase = 'failed';
  } else {
    phase = 'downloading';
  }

  return {
    key: download.id,
    id: download.id,
    phase,
    label: download.fileName,
    fileName: download.fileName,
    progress: download.progress,
    loaded: download.loaded,
    total: download.total,
    successHeadline: download.successHeadline,
    totalFiles: download.totalFiles,
    error: typeof download.error === 'string' ? download.error : download.error?.message,
    isDismissing: download.isDismissing,
    remove: onRemoveFileDownload,
  };
};

const DownloadTray = ({
  folderDownloads = [],
  fileDownloads = [],
  downloadProgress = {},
  onClose,
  onRemoveDownload,
  onRemoveFileDownload,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);

  const toggleMinimize = () => setIsMinimized((prev) => !prev);

  // Folder downloads (each maps to a single normalized card through all phases)
  const folderItems = folderDownloads.map((d) => normalizeFolderDownload(d, downloadProgress, onRemoveDownload));

  // File downloads (direct file blob downloads shown in the tray)
  const fileItems = fileDownloads
    .filter((d) => d.showProgressBar || d.downloadSuccess)
    .map((d) => normalizeFileDownload(d, onRemoveFileDownload));

  // Background downloadProgress entries that are not linked to any folderDownload
  // (edge-case safety net for orphaned blob transfers)
  const folderIds = new Set(folderDownloads.map((d) => d.id));
  const backgroundItems = Object.entries(downloadProgress)
    .filter(([id]) => !folderIds.has(id))
    .map(([id, p]) => ({
      key: id,
      id,
      phase: 'downloading',
      label: p.fileName?.split('_')[0] || 'Downloading...',
      fileName: p.fileName,
      progress: p.progress ?? 0,
      loaded: p.loaded,
      total: p.total,
      isDismissing: false,
      remove: onRemoveDownload,
    }));

  const allItems = [...folderItems, ...fileItems, ...backgroundItems];
  const hasAnyDownloads = allItems.length > 0;

  if (!hasAnyDownloads) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 10000,
        minWidth: '350px',
        maxWidth: '400px',
      }}
    >
      <div
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          maxWidth: '360px',
        }}
        className="p-0 card-bg"
      >
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center inner-border-bottom"
          style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}
        >
          <span style={{ fontWeight: '600', fontSize: '14px' }}>Downloads</span>
          <div className="d-flex" style={{ gap: '4px' }}>
            <Button
              icon={isMinimized ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}
              className="p-button-text p-button-sm"
              style={{ color: 'white', padding: '4px' }}
              onClick={toggleMinimize}
              tooltip={isMinimized ? 'Expand' : 'Minimize'}
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              icon="fas fa-times"
              className="p-button-text p-button-sm"
              style={{ color: 'white', padding: '4px' }}
              onClick={onClose}
              tooltip="Close"
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        </div>

        {/* Body */}
        {!isMinimized && (
          <div style={{ padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
            {allItems.map(({ key, ...props }) => (
              <FloatingProgressBar key={key} {...props} />
            ))}
          </div>
        )}
      </div>
      <Tooltip target=".p-button" />
    </div>
  );
};

export default DownloadTray;
